import React, { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  XMarkIcon,
  ArrowPathIcon,
  PlayIcon,
  CheckCircleIcon,
  ChevronDownIcon,
  ArrowDownTrayIcon,
  PencilIcon,
  FilmIcon,
  SpeakerWaveIcon,
} from '@heroicons/react/24/outline';
import { toast } from 'react-hot-toast';
import { videoApi } from '@/services/videoApi';
import { voiceApi } from '@/services/audioApi';

interface StepDetailModalProps {
  jobId: string;
  job: any;
  step: any;
  onClose: () => void;
  onChanged: () => void;
}

/**
 * Expanded, full-size view of a single pipeline step: shows the step's text
 * output (transcription/translation) in a clearly readable, editable area,
 * lets the user either recompute the step with a different provider or save
 * an edit and continue the pipeline from the next step (without redoing this
 * one), and surfaces links to the source/intermediate media files.
 */
const StepDetailModal: React.FC<StepDetailModalProps> = ({ jobId, job, step, onClose, onChanged }) => {
  const isSTTStep = step.id === 'step-3';
  const isTranslationStep = step.id === 'step-4';
  const isTTSStep = step.id === 'step-5';
  const isLipsyncStep = step.id === 'step-6';
  const lipsyncModelSelectable = isLipsyncStep && job?.request?.videoMode !== 'dubbed';
  const textField: 'transcribedText' | 'translatedText' | null = isSTTStep
    ? 'transcribedText'
    : isTranslationStep
    ? 'translatedText'
    : null;

  const jobIsActive = job?.status === 'processing' || job?.status === 'pending';
  const stepIsEditable = step.status === 'completed' && !jobIsActive;
  const canRecompute = step.canRerun && (step.status === 'completed' || step.status === 'failed') && !jobIsActive;

  const textStatusLabel = () => {
    if (jobIsActive) return 'Job is processing — wait for it to finish this stage';
    if (step.status === 'failed') return 'This step failed — output is read-only';
    if (step.status !== 'completed') return 'Available once this step completes';
    return '';
  };

  const [text, setText] = useState<string>(textField ? (step.data?.[textField] ?? '') : '');
  const [sttProviderOverride, setSttProviderOverride] = useState<'sarvam' | 'ai4bharat'>(job?.request?.sttProvider ?? 'sarvam');
  const [translationProviderOverride, setTranslationProviderOverride] = useState<'sarvam' | 'google' | 'deepl'>(job?.request?.translationProvider ?? 'sarvam');
  const [voiceOverride, setVoiceOverride] = useState<string>(job?.request?.voiceId ?? '');
  const [lipsyncModelOverride, setLipsyncModelOverride] = useState<'wav2lip' | 'video-retalking'>(job?.request?.lipsyncModel ?? 'video-retalking');

  const { data: voicesData } = useQuery(['voices'], async () => (await voiceApi.getVoices()).data, { enabled: isTTSStep });
  const voices: any[] = voicesData?.voices ?? voicesData ?? [];

  const continueMutation = useMutation({
    mutationFn: () => videoApi.continueStep(jobId, step.id, textField ? { [textField]: text } : undefined),
    onSuccess: () => {
      toast.success('Saved — continuing from the next step');
      onChanged();
      onClose();
    },
    onError: () => toast.error('Failed to save and continue'),
  });

  const recomputeMutation = useMutation({
    mutationFn: (args: { overrides?: Record<string, string>; runAlone?: boolean }) =>
      videoApi.executeStep(jobId, step.id, args.overrides, args.runAlone),
    onSuccess: (_data, variables) => {
      toast.success(variables.runAlone ? 'Running this step only' : 'Step re-run started');
      onChanged();
      onClose();
    },
    onError: () => toast.error('Failed to re-run step'),
  });

  // Related-file links, resolved from whichever steps have produced them so far.
  const step1 = job?.steps?.find((s: any) => s.id === 'step-1');
  const step2 = job?.steps?.find((s: any) => s.id === 'step-2');
  const step5 = job?.steps?.find((s: any) => s.id === 'step-5');
  const uploadFileId: string | undefined = job?.request?.uploadFileId;
  const extractedAudioUrl: string | undefined = step2?.data?.extractedAudioUrl;
  const translatedAudioUrl: string | undefined = step5?.data?.translatedAudioUrl;

  const [audioPreview, setAudioPreview] = useState<{ key: string; url: string } | null>(null);
  const [loadingAudioKey, setLoadingAudioKey] = useState<string | null>(null);

  const openUploadedVideo = async () => {
    if (!uploadFileId) return;
    try {
      const res = await videoApi.getUploadShareableUrl(uploadFileId);
      if (res?.url) window.open(res.url, '_blank', 'noopener');
    } catch {
      toast.error('Failed to open uploaded video');
    }
  };

  const previewAudio = async (key: 'extracted' | 'translated', apiUrl: string) => {
    setLoadingAudioKey(key);
    try {
      // apiUrl looks like /api/video/audio/{jobId}/{filename}
      const parts = apiUrl.split('/').filter(Boolean);
      const filename = parts[parts.length - 1];
      const blobUrl = await videoApi.getAudioBlobUrl(jobId, filename);
      setAudioPreview({ key, url: blobUrl });
    } catch {
      toast.error('Audio not available — it may have been cleaned up');
    } finally {
      setLoadingAudioKey(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm" onClick={onClose}>
      <div
        className="w-full max-w-3xl max-h-[90vh] overflow-y-auto bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between p-5 border-b border-gray-100 dark:border-gray-700 sticky top-0 bg-white dark:bg-gray-800 z-10">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{step.name}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{step.description}</p>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Full, readable text with edit + continue */}
          {textField && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {isSTTStep ? 'Transcribed text' : 'Translated text'}
                </label>
                {!stepIsEditable && (
                  <span className="text-xs text-gray-400">
                    {textStatusLabel()}
                  </span>
                )}
              </div>
              <textarea
                value={text}
                onChange={(e) => setText(e.target.value)}
                disabled={!stepIsEditable || continueMutation.isLoading}
                rows={14}
                className="w-full text-base leading-relaxed p-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-900 text-gray-900 dark:text-white resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-70 disabled:bg-gray-50 dark:disabled:bg-gray-800"
                placeholder={stepIsEditable ? 'No text yet' : ''}
              />
              {/* Step-level error */}
              {step.error && (
                <div className="mt-2 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-xs text-red-600 dark:text-red-400">
                  Error: {step.error}
                </div>
              )}

              {stepIsEditable && (
                <div className="mt-2 flex items-center gap-2">
                  <button
                    type="button"
                    disabled={continueMutation.isLoading || !text.trim()}
                    onClick={() => continueMutation.mutate()}
                    className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                  >
                    {continueMutation.isLoading
                      ? <ArrowPathIcon className="h-4 w-4 animate-spin" />
                      : <PencilIcon className="h-4 w-4" />}
                    Save &amp; continue from next step
                  </button>
                  <span className="text-xs text-gray-400">Keeps this step&apos;s edit and resumes the pipeline — doesn&apos;t redo this step.</span>
                </div>
              )}
            </div>
          )}

          {/* Recompute this step with a different provider */}
          {canRecompute && (isSTTStep || isTranslationStep || isTTSStep) && (
            <div className="border-t border-gray-100 dark:border-gray-700 pt-4">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                Or recompute this step from scratch
              </p>

              {isSTTStep && (
                <div className="space-y-2">
                  <div className="grid grid-cols-2 gap-2">
                    {(['sarvam', 'ai4bharat'] as const).map((p) => (
                      <button
                        key={p}
                        type="button"
                        onClick={() => setSttProviderOverride(p)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border-2 text-xs font-medium transition-all ${
                          sttProviderOverride === p
                            ? 'border-blue-600 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-200'
                            : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400'
                        }`}
                      >
                        {p === 'sarvam' ? 'Sarvam AI' : 'AI4Bharat'}
                        {sttProviderOverride === p && <CheckCircleIcon className="h-3.5 w-3.5 ml-auto" />}
                      </button>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      disabled={recomputeMutation.isLoading}
                      onClick={() => recomputeMutation.mutate({ overrides: { sttProvider: sttProviderOverride }, runAlone: true })}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
                    >
                      {recomputeMutation.isLoading ? <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> : <PlayIcon className="h-3.5 w-3.5" />}
                      Run this step only
                    </button>
                    <button
                      type="button"
                      disabled={recomputeMutation.isLoading}
                      onClick={() => recomputeMutation.mutate({ overrides: { sttProvider: sttProviderOverride } })}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-700 hover:bg-gray-800 text-white text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
                    >
                      {recomputeMutation.isLoading ? <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> : <PlayIcon className="h-3.5 w-3.5" />}
                      Run &amp; continue pipeline
                    </button>
                  </div>
                </div>
              )}

              {isTranslationStep && (
                <div className="space-y-2">
                  <div className="grid grid-cols-3 gap-2">
                    {([
                      { id: 'sarvam', label: 'Sarvam AI' },
                      { id: 'google', label: 'Google' },
                      { id: 'deepl', label: 'DeepL' },
                    ] as const).map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => setTranslationProviderOverride(p.id)}
                        className={`flex items-center gap-1.5 px-2 py-2 rounded-lg border-2 text-xs font-medium transition-all ${
                          translationProviderOverride === p.id
                            ? 'border-indigo-600 bg-indigo-100 dark:bg-indigo-900/40 text-indigo-700 dark:text-indigo-200'
                            : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400'
                        }`}
                      >
                        {p.label}
                        {translationProviderOverride === p.id && <CheckCircleIcon className="h-3 w-3 ml-auto" />}
                      </button>
                    ))}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      disabled={recomputeMutation.isLoading}
                      onClick={() => recomputeMutation.mutate({ overrides: { translationProvider: translationProviderOverride }, runAlone: true })}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
                    >
                      {recomputeMutation.isLoading ? <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> : <PlayIcon className="h-3.5 w-3.5" />}
                      Run this step only
                    </button>
                    <button
                      type="button"
                      disabled={recomputeMutation.isLoading}
                      onClick={() => recomputeMutation.mutate({ overrides: { translationProvider: translationProviderOverride } })}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-700 hover:bg-gray-800 text-white text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
                    >
                      {recomputeMutation.isLoading ? <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> : <PlayIcon className="h-3.5 w-3.5" />}
                      Run &amp; continue pipeline
                    </button>
                  </div>
                </div>
              )}

              {isTTSStep && (
                <div className="space-y-2">
                  <div className="relative">
                    <select
                      value={voiceOverride}
                      onChange={(e) => setVoiceOverride(e.target.value)}
                      className="w-full px-3 py-2 pr-8 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white appearance-none"
                    >
                      <option value="" disabled>Select voice…</option>
                      {voices.map((v: any) => (
                        <option key={v._id || v.id} value={v._id || v.id}>{v.name}</option>
                      ))}
                    </select>
                    <ChevronDownIcon className="absolute right-2 top-2.5 h-4 w-4 text-gray-400 pointer-events-none" />
                  </div>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      disabled={recomputeMutation.isLoading || !voiceOverride}
                      onClick={() => recomputeMutation.mutate({ overrides: { voiceId: voiceOverride }, runAlone: true })}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
                    >
                      {recomputeMutation.isLoading ? <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> : <PlayIcon className="h-3.5 w-3.5" />}
                      Run this step only
                    </button>
                    <button
                      type="button"
                      disabled={recomputeMutation.isLoading || !voiceOverride}
                      onClick={() => recomputeMutation.mutate({ overrides: { voiceId: voiceOverride } })}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-700 hover:bg-gray-800 text-white text-xs font-medium rounded-lg transition-colors disabled:opacity-50"
                    >
                      {recomputeMutation.isLoading ? <ArrowPathIcon className="h-3.5 w-3.5 animate-spin" /> : <PlayIcon className="h-3.5 w-3.5" />}
                      Run &amp; continue pipeline
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Plain rerun for steps with no text/provider choice (audio extraction,
              video generation, final assembly) — lipsync/avatar video generation
              also gets an engine selector, matching the STT/Translation/TTS steps above. */}
          {canRecompute && !isSTTStep && !isTranslationStep && !isTTSStep && (
            <div className="border-t border-gray-100 dark:border-gray-700 pt-4 space-y-3">
              {lipsyncModelSelectable && (
                <div className="grid grid-cols-2 gap-2">
                  {([
                    { id: 'video-retalking', label: 'Video-ReTalking' },
                    { id: 'wav2lip', label: 'Wav2Lip (Faster)' },
                  ] as const).map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => setLipsyncModelOverride(m.id)}
                      className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border-2 text-xs font-medium transition-all ${
                        lipsyncModelOverride === m.id
                          ? 'border-gray-700 bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100'
                          : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400'
                      }`}
                    >
                      {m.label}
                      {lipsyncModelOverride === m.id && <CheckCircleIcon className="h-3.5 w-3.5 ml-auto" />}
                    </button>
                  ))}
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={recomputeMutation.isLoading}
                  onClick={() => recomputeMutation.mutate({
                    overrides: lipsyncModelSelectable ? { lipsyncModel: lipsyncModelOverride } : undefined,
                    runAlone: true,
                  })}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                >
                  {recomputeMutation.isLoading ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <PlayIcon className="h-4 w-4" />}
                  Run this step only
                </button>
                <button
                  type="button"
                  disabled={recomputeMutation.isLoading}
                  onClick={() => recomputeMutation.mutate({
                    overrides: lipsyncModelSelectable ? { lipsyncModel: lipsyncModelOverride } : undefined,
                  })}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-700 hover:bg-gray-800 text-white text-sm font-medium rounded-lg transition-colors disabled:opacity-50"
                >
                  {recomputeMutation.isLoading ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <PlayIcon className="h-4 w-4" />}
                  Run &amp; continue pipeline
                </button>
              </div>
            </div>
          )}

          {/* Related files */}
          {(uploadFileId || extractedAudioUrl || translatedAudioUrl) && (
            <div className="border-t border-gray-100 dark:border-gray-700 pt-4">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">
                Source &amp; intermediate files
              </p>
              <div className="space-y-2">
                {uploadFileId && (
                  <button
                    type="button"
                    onClick={openUploadedVideo}
                    className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    <FilmIcon className="h-4 w-4" />
                    View uploaded video
                  </button>
                )}

                {extractedAudioUrl && (
                  <div>
                    {audioPreview?.key === 'extracted' ? (
                      <audio controls src={audioPreview.url} className="w-full h-9" />
                    ) : (
                      <button
                        type="button"
                        disabled={loadingAudioKey === 'extracted'}
                        onClick={() => previewAudio('extracted', extractedAudioUrl)}
                        className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50"
                      >
                        {loadingAudioKey === 'extracted' ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <SpeakerWaveIcon className="h-4 w-4" />}
                        Preview extracted audio
                      </button>
                    )}
                  </div>
                )}

                {translatedAudioUrl && (
                  <div>
                    {audioPreview?.key === 'translated' ? (
                      <audio controls src={audioPreview.url} className="w-full h-9" />
                    ) : (
                      <button
                        type="button"
                        disabled={loadingAudioKey === 'translated'}
                        onClick={() => previewAudio('translated', translatedAudioUrl)}
                        className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50"
                      >
                        {loadingAudioKey === 'translated' ? <ArrowPathIcon className="h-4 w-4 animate-spin" /> : <ArrowDownTrayIcon className="h-4 w-4" />}
                        Preview translated audio
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default StepDetailModal;
